
import { AxiosInstance } from 'axios';
import { parseBoolean } from "../utils/utils";
import AxiosMockAdapter from 'axios-mock-adapter';

class MockApi {
    private apiMockAdapter: AxiosMockAdapter;
    private apiCallCount: Map<string, number> = new Map(); // Note: This will reset on page refresh

    private makeApiCallKey(method: string, url: string): string {
        return method + ":" + url;
    }

    private incrementApiCallCount(method: string, url: string) {
        const key = this.makeApiCallKey(method, url);
        const count = this.apiCallCount.get(key) || 0;
        this.apiCallCount.set(key, count + 1);
    }

    getApiCallCount(method: string, url: string): number {
        const key = this.makeApiCallKey(method, url);
        return this.apiCallCount.get(key) || 0;
    }

    private preProcessRequest(config: any) {
        console.log("Mock API: " + config.method + " " + config.url + " #" + this.getApiCallCount(config.method, config.url));
        this.incrementApiCallCount(config.method, config.url);
        console.log("Mock API: " + config.method + " " + config.url + " #" + this.getApiCallCount(config.method, config.url));
    }

    constructor(apiClient: AxiosInstance) {
        this.apiMockAdapter = new AxiosMockAdapter(apiClient);
    }

    installHandlers() {
        console.log("Installing mock API handlers");

        this.installLoginHandler();
        this.installGetProgramStateLicenseResearchDataHandler();
        this.installLicenseResearchDataFeedbackUpdateHandler();
        this.installGetLicensureResearchDataListHandler();

        // Match ALL requests
        this.apiMockAdapter.onAny().reply((config) => {
            console.log("Mock API: Unexpected request", config.method, ": ", config.url);
            // Unexpected request, error out
            return [500, {}];
        });
    }

    installLoginHandler() {
        this.apiMockAdapter.onPost("/api/v1/user/login").reply(200, {
            status: "success",
            data: {
                accessToken: "sampleAccessToken123",
                userData: {
                    id: "user123",
                    name: "John Doe",
                    email: "john.doe@example.com",
                },
            },
            error: {
                message: "",
            },
        });
    }

    installGetProgramStateLicenseResearchDataHandler() {
        // Respond after a latency
        const replyLatencyMs = 1000;
        this.apiMockAdapter.onGet(/\/api\/v1\/licensure\/research-data\/next(\?.*)?$/).reply((config) => {
            console.log("Mock API: " + config.method + " " + config.url);
            return new Promise((resolve) => {
                setTimeout(() => {
                    resolve([200, {
                        status: "success",
                        data: {
                            id: "123",
                            version: 1,
                            lastUpdatedDate: "2023-01-01",
                            program: {
                                providerOrg: { name: "Rock School", state: { code: "FL" } },
                                programName: "Violin"
                            },
                            stateLicensureStatus: {
                                state: { code: "CA" },
                                meetRequirements: true
                            },
                            referenceData: [
                                { url: "http://example.com/1", note: "Mock Note 1" },
                                { url: "http://example.com/2", note: "Mock Note 2" },
                                { url: "http://example.com/3", note: "Mock Note 3" },
                            ]
                        },
                        error: {
                            message: "",
                        }
                    }]);
                }, replyLatencyMs);
            });
        });
    }

    installLicenseResearchDataFeedbackUpdateHandler() {
        // Respond after a latency
        const replyLatencyMs = 5000;

        // Alternate between success and error
        this.apiMockAdapter.onPost("/api/v1/licensure/research-data/feedback").reply((config) => {
            this.preProcessRequest(config);

            let shouldReturnErr = false;

            if (config.method && config.url) {
                shouldReturnErr = (this.getApiCallCount(config.method, config.url) % 2 === 1);
            }

            if (shouldReturnErr) {
                return new Promise((resolve) => {
                    setTimeout(() => {
                        resolve([500, {
                            status: "error",
                            error: {
                                message: "Mocked error message",
                            },
                        }]);
                    }, replyLatencyMs);
                });
            }

            return new Promise((resolve) => {
                setTimeout(() => {
                    resolve([200, {
                        status: "success",
                    }]);
                }, replyLatencyMs);
            });

        });

    }

    installGetLicensureResearchDataListHandler() {
        // Respond after a latency
        const replyLatencyMs = 1000;
        this.apiMockAdapter.onGet(/\/api\/v1\/licensure\/research-data\/list(\?.*)?$/).reply(() => {
            console.log("Mock API: GET /api/v1/licensure/research-data/list");
            /* To test empty content */
            /*
            return new Promise((resolve) => {
                setTimeout(() => {
                    resolve([200, {
                        status: "success",
                        data: {
                            items: []
                        }
                    }]);
                }, replyLatencyMs);
            });
            */

            return new Promise((resolve) => {
                setTimeout(() => {
                    resolve([200, {
                        status: "success",
                        data: {
                            items: [{
                                id: "123",
                                version: 1,
                                lastUpdatedDate: "2023-01-01",
                                program: {
                                    providerOrg: { name: "Rock School", state: { code: "FL" } },
                                    programName: "Violin"
                                },
                                stateLicensureStatus: {
                                    state: { code: "CA" },
                                    meetRequirements: true
                                },
                                referenceData: [
                                    { url: "http://example.com/1", note: "Mock Note 1" },
                                    { url: "http://example.com/2", note: "Mock Note 2" },
                                    { url: "http://example.com/3", note: "Mock Note 3" },
                                ]
                            }],
                        },
                        error: {
                            message: "",
                        }
                    }]);
                }, replyLatencyMs);
            });
        });
    }
}

function initMockApi(apiClient: AxiosInstance) {
    if (parseBoolean(process.env.REACT_APP_USE_MOCK_API)) {
        mockApi = new MockApi(apiClient);
        mockApi.installHandlers();
    }
}

var mockApi: MockApi

export { initMockApi };
